import { useCallback, useMemo, useState } from 'react';

import { useBridgeApi } from '@/api/BridgeApi.ts';
import { useBridgewayApi } from '@/api/BridgewayApi.ts';
import { useUserId } from '@/auth/UserIdContext';
import { logger } from '@/logger';
import { useResourceList } from '@/spaces/people/useResourceList';
import { trackEvent } from '@/utils/fullstory';

import type {
	Audience,
	AudiencePatch,
	PaginatedAudiencesResponse,
} from './types';

const AUDIENCES_PAGE_SIZE = 50;

interface UseAudiencesTabArgs {
	selectedAudienceId: string | null;
	onSelectAudience: (id: string | null) => void;
	draftAudience: Audience | null;
	onDraftChange: (draft: Audience | null) => void;
}

export const useAudiencesTab = ({
	selectedAudienceId,
	onSelectAudience,
	draftAudience,
	onDraftChange,
}: UseAudiencesTabArgs) => {
	const bridgeApi = useBridgeApi();
	const bridgewayApi = useBridgewayApi();
	const userId = useUserId();

	const [isSavingAudience, setIsSavingAudience] = useState(false);
	const [saveError, setSaveError] = useState<string | null>(null);

	const paginatedPath = useCallback(
		(uid: string, params: URLSearchParams) =>
			`/api/user/${encodeURIComponent(uid)}/audiences/paginated?${params.toString()}`,
		[],
	);
	const detailPath = useCallback(
		(uid: string, id: string) =>
			`/api/user/${encodeURIComponent(uid)}/audiences/${encodeURIComponent(id)}`,
		[],
	);
	const extractItems = useCallback(
		(response: PaginatedAudiencesResponse) => response.audiences,
		[],
	);
	const getId = useCallback((audience: Audience) => audience.id, []);
	const errorMessages = useMemo(
		() => ({
			listLoadFailed: 'Failed to load audiences. Please try again.',
			itemLoadFailed: 'Audience not found or failed to load.',
		}),
		[],
	);

	const list = useResourceList<Audience, PaginatedAudiencesResponse>({
		userId,
		selectedId: selectedAudienceId,
		pageSize: AUDIENCES_PAGE_SIZE,
		logLabel: 'audiences',
		paginatedPath,
		detailPath,
		extractItems,
		getId,
		errorMessages,
	});

	const handleAudienceClick = useCallback(
		(audience: Audience) => {
			trackEvent('Audience Detail Viewed', { audience_id: audience.id });
			onSelectAudience(audience.id);
			bridgeApi.sendAction({
				type: 'do_navigate_to_audience_detail',
				audience_id: audience.id,
			});
		},
		[bridgeApi, onSelectAudience],
	);

	const handleClosePanel = useCallback(() => {
		if (draftAudience) {
			onDraftChange(null);
			setSaveError(null);
			return;
		}
		onSelectAudience(null);
		bridgeApi.sendAction({
			type: 'do_navigate_to_audience_detail',
			audience_id: null,
		});
	}, [bridgeApi, draftAudience, onDraftChange, onSelectAudience]);

	const handleCreate = useCallback(() => {
		onDraftChange({
			id: '',
			user_id: userId,
			name: '',
			description: '',
			source: 'manual',
			created_by: 'user',
			created_at: new Date().toISOString(),
			updated_at: new Date().toISOString(),
		});
		setSaveError(null);
	}, [onDraftChange, userId]);

	const handleDraftSave = useCallback(
		async (updates: AudiencePatch) => {
			if (!userId) return;

			setIsSavingAudience(true);
			setSaveError(null);
			try {
				const body = {
					name: updates.name ?? draftAudience?.name ?? '',
					description: updates.description ?? draftAudience?.description ?? '',
				};
				const created = await bridgewayApi.post<Audience>(
					`/api/user/${encodeURIComponent(userId)}/audiences/`,
					body,
				);
				list.prependItem(created);
				onDraftChange(null);
				onSelectAudience(created.id);
				trackEvent('Audience Created', { audience_id: created.id });
				bridgeApi.sendAction({
					type: 'do_navigate_to_audience_detail',
					audience_id: created.id,
				});
			} catch (err) {
				logger.error('Failed to create audience', { error: err });
				const message =
					err instanceof Error ? err.message : 'Failed to create audience';
				setSaveError(message);
				throw err;
			} finally {
				setIsSavingAudience(false);
			}
		},
		[
			bridgeApi,
			bridgewayApi,
			draftAudience,
			list,
			onDraftChange,
			onSelectAudience,
			userId,
		],
	);

	const handleAudienceDelete = useCallback(async () => {
		if (!userId || !selectedAudienceId) return;

		try {
			await bridgewayApi.delete(
				`/api/user/${encodeURIComponent(userId)}/audiences/${encodeURIComponent(selectedAudienceId)}`,
			);
			trackEvent('Audience Deleted', { audience_id: selectedAudienceId });
			list.removeItem(selectedAudienceId);
			onSelectAudience(null);
			bridgeApi.sendAction({
				type: 'do_navigate_to_audience_detail',
				audience_id: null,
			});
		} catch (err) {
			logger.error('Failed to delete audience', { error: err });
			setSaveError('Failed to delete audience. Please try again.');
		}
	}, [
		bridgeApi,
		bridgewayApi,
		list,
		onSelectAudience,
		selectedAudienceId,
		userId,
	]);

	const handleAudienceSave = useCallback(
		async (updates: AudiencePatch) => {
			if (!userId || !selectedAudienceId) return;

			setIsSavingAudience(true);
			setSaveError(null);
			try {
				const updated = await bridgewayApi.patch<Audience>(
					`/api/user/${encodeURIComponent(userId)}/audiences/${encodeURIComponent(selectedAudienceId)}`,
					updates,
				);
				list.updateItem(updated);
				trackEvent('Audience Updated', { audience_id: updated.id });
			} catch (err) {
				logger.error('Failed to update audience', { error: err });
				const message =
					err instanceof Error ? err.message : 'Failed to update audience';
				setSaveError(message);
				throw err;
			} finally {
				setIsSavingAudience(false);
			}
		},
		[bridgewayApi, list, selectedAudienceId, userId],
	);

	return {
		list,
		isSavingAudience,
		saveError,
		handleAudienceClick,
		handleClosePanel,
		handleAudienceSave,
		handleAudienceDelete,
		handleCreate,
		handleDraftSave,
	};
};
